/**
 * Manage what parts of our application should be updated and when. The goal
 * of this component it to ensure that the user has the latest version of the
 * data and code that they need to use our site.
 *
 * The function of this component is detailed in ADR #301:
 * https://docs.google.com/document/d/1FstRfSQ90iadq7ywGRPksS89AO9asxuKd-N3nDnW2zg/edit
 *
 * In short:
 *  - We frequently check to see if the user's KAAC cookie has changed - if so
 *    we assume that something important about the user has changed and we
 *    should probably reload the page as a result.
 *  - We check to see if there has been a new publish of content to the site
 *    and if so we clear our Apollo GraphQL cache to ensure we are getting the
 *    latest version of the content.
 *  - We check to see if there has been a new static deploy to the site and if
 *    so we try to get the page to reload.
 *  - Additionally, we periodically update the user profile data and the
 *    urgent notifications if the user is still active on the site, to ensure
 *    that they have the latest information.
 *
 *  To get the page to reload we tell the `history` module to forceRefresh,
 *  which will cause a full page load (rather than a client-side nav) the next
 *  time a client-side nav would've occurred. If that doesn't work we then
 *  attempt a series of increasingly aggressive modals to encourage the user
 *  to reload the page.
 */
import {compose} from "mu-lambda";
import * as React from "react";

import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import Log from "@khan/logging";
import {DeployInfo, RequestInfo} from "@khan/shared-core";

import {
    getKAAC,
    getLatestVersions,
    isPageHidden,
} from "./periodic-update-utils.ts";
import withInteractedRecently, {
    type WithInteractedRecentlyProps,
    type WithoutInteractedRecently,
} from "./with-interacted-recently.tsx";

// How long to wait before we can force a refresh from a static version change
// or a publish (30 mins)
export const LATEST_VERSION_REFRESH_DELAY = 30 * 60 * 1000;

type OwnProps = {
    // When false, the functionality of this component that turns client-side
    // navigations into full page refreshes will be disabled. When true, the
    // internally-computed forceRefresh value will be used. This prop is
    // optional and defaults to true if not specified.
    allowForceRefresh: boolean;
    children: (arg1: {forceRefresh: boolean}) => React.ReactNode;
};

type Props = OwnProps & WithActionSchedulerProps & WithInteractedRecentlyProps;

type DefaultProps = {
    allowForceRefresh: Props["allowForceRefresh"];
};

type State = {
    forceRefresh: boolean;
};

class PeriodicUpdate extends React.Component<Props, State> {
    INITIAL_KAAC: string | null | undefined;
    // @ts-expect-error - TS2564 - Property 'LATEST_COMMIT' has no initializer and is not definitely assigned in the constructor.
    LATEST_COMMIT: string;
    refreshModalPromise: Promise<any> | null | undefined;

    static defaultProps: DefaultProps = {
        allowForceRefresh: true,
    };

    state: State = {
        forceRefresh: false,
    };

    componentDidMount() {
        // Initialize time and data for later comparison
        this.INITIAL_KAAC = getKAAC();
        this.LATEST_COMMIT = DeployInfo.commitSHA;

        // Attach interval timers that will periodically update to check on
        // various conditions to figure out if we should refresh the page,
        // show an overlay, or update user data.
        const {schedule} = this.props;
        schedule.interval(this.checkForceRefresh, 1000); // 1 second
        // We delay starting to check deploys and publishes by 30mins as
        // we don't need the user immediately refreshing the page.
        schedule.timeout(() => {
            schedule.interval(this.checkDeploysAndPublishes, 60 * 1000); // 1 minute
        }, LATEST_VERSION_REFRESH_DELAY);
        schedule.interval(this.checkUserDataUpdate, 10 * 60 * 1000); // 10 minutes

        // Clean up the interval checks when we're leaving the page
        window.addEventListener("beforeunload", this.cancelIntervals);
    }

    componentWillUnmount() {
        // Remove the listeners on unmount
        window.removeEventListener("beforeunload", this.cancelIntervals);
    }

    /**
     * Log strings to the console (mainly used for debugging and tracking
     * usage).
     *
     * @param {string} msg the message to log
     */
    log(msg: string) {
        const logMessage = `Periodic Update: ${msg}`;

        Log.log(logMessage);
    }

    /**
     * Cancel all of the interval checks. We run this when it looks like the
     * user is leaving the page (to avoid having the overlay accidentally
     * pop up as they're logging out).
     */
    cancelIntervals: () => void = () => {
        this.props.schedule.clearAll();
    };

    /**
     * A way to safely set state.
     *
     * Ignores any failed attempts to set state. This appears to happen
     * in very rare instances for some users when their pages have
     * been open for a very long time.
     *
     * @param {State} state the state to set.
     */
    safelySetState(state: Partial<State>): void {
        try {
            // @ts-expect-error - TS2345 - Argument of type 'Partial<State>' is not assignable to parameter of type 'State | ((prevState: Readonly<State>, props: Readonly<Props>) => State | Pick<State, keyof State> | null) | Pick<...> | null'.
            this.setState(state);
        } catch (e: any) {
            // Ignored. See above.
            this.log("Failed to safely set state.");
        }
    }

    /**
     * Determine if we should force a refresh of the page (either by forcing
     * the page to refresh on client-side navigation or by showing a modal
     * overlay to the user and asking them to manually reload the page).
     */
    checkForceRefresh: () => void = () => {
        const {forceRefresh} = this.state;
        if (!forceRefresh) {
            // Check to see if we need to force a page refresh
            // We do that by comparing the KAAC cookie that we had initially
            // against the cookie that currently exists
            if (getKAAC() !== this.INITIAL_KAAC) {
                this.log("KAAC Cookie Changed, Force Refresh");
                // If there were any differences then we try to page refresh
                this.safelySetState({forceRefresh: true});
            }
        }
    };

    /**
     * Check for new deploys and content publishes and use that to potentially
     * force a page refresh or clear internal Apollo caches.
     */
    checkDeploysAndPublishes: () => void = () => {
        // If we're already forcing a refresh then we don't need to keep
        // checking to see if the versions have changed. Or if we're running
        // inside an E2E test then we don't want a publish/deploy to
        // accidentally cause problems for the tests.
        if (this.state.forceRefresh || RequestInfo.isE2E || RequestInfo.isBot) {
            return;
        }

        // Run a query getting the latest publish commit and static version
        // These pieces of information influence if we should be forcing the
        // page to refresh or clearing our internal datastores.
        getLatestVersions()
            .then(({currentStaticVersion, currentCommitSHA}) => {
                // We check if there has been a deploy of the static site
                // and if so we force a refresh (as there may be new logic
                // that we want the user to use).
                if (currentStaticVersion !== DeployInfo.staticVersion) {
                    this.log("Static Version Changed, Force Refresh");
                    this.safelySetState({forceRefresh: true});
                }

                // We check to see if there's been a new publish of content
                // (we determine this by comparing our last publish commit
                // with the current one). If there has been a publish then
                // we clear the Apollo Cache and all content data caches. This
                // is very heavy-handed and we end up re-requesting more data
                // than we should but we don't have a good way of knowing which
                // data is affected by a publish so we have to clear it all.
                if (currentCommitSHA !== this.LATEST_COMMIT) {
                    this.log("Clearing Apollo & Content Data Caches");

                    // Remember the commit for later, to make sure we don't
                    // clear the cache too often
                    this.LATEST_COMMIT = currentCommitSHA;
                }
            })
            .catch(() => {
                // We intentionally ignore server errors, we'll be re-querying
                // frequently so it's safe if we fail temporarily
            });
    };

    /**
     * Determine if we should attempt to dynamically update any user-specific
     * information (such as the user profile).
     */
    checkUserDataUpdate: () => void = () => {
        const {hasInteractedRecently} = this.props;
        const {forceRefresh} = this.state;
        // If the page is visible, we aren't waiting for the page to refresh,
        // and the user has interacted with the page recently then we use this
        // opportunity to update the urgent notifications and user's profile
        if (!isPageHidden() && !forceRefresh && hasInteractedRecently) {
            this.log("Updating User Details");
        }
    };

    render(): React.ReactNode {
        const {forceRefresh} = this.state;
        const {allowForceRefresh} = this.props;

        const doForceRefresh = allowForceRefresh && forceRefresh;

        return <>{this.props.children({forceRefresh: doForceRefresh})}</>;
    }
}

type ExportProps = WithoutInteractedRecently<
    WithoutActionScheduler<
        JSX.LibraryManagedAttributes<
            typeof PeriodicUpdate,
            React.ComponentProps<typeof PeriodicUpdate>
        >
    >
>;

export default compose(
    withActionScheduler,
    withInteractedRecently(),
)(PeriodicUpdate) as React.ComponentType<ExportProps>;
